/**
 * Agreement Service
 * Fetches signed/draft agreement documents via WebSocket (DocumentRequest push pattern).
 */

import { getRuntimeConfig } from '../config/runtime';
import { generateID } from '../utils/generateID';
import { logger } from '../utils/logger';
import socketService from './socketService';

const REQUEST_TIMEOUT = 15000;

export interface AgreementDocument {
  id: string;
  documentType: string;
  fileName: string;
  agreementVersion: string;
  signedBy: string;
  signedAt: string;
  downloadUrl: string;
}

export interface S3Document {
  key: string;
  fileName: string;
  size: number;
  lastModified: string;
  downloadUrl: string;
}

/**
 * Send a DocumentRequest action and wait for the DocumentResponse push.
 */
function sendDocumentRequest(action: string, extra: Record<string, any> = {}): Promise<any> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      socketService.off('document:response', handler);
      reject(new Error(`DocumentRequest ${action} timed out after ${REQUEST_TIMEOUT}ms`));
    }, REQUEST_TIMEOUT);

    const handler = (message: any) => {
      clearTimeout(timeout);
      socketService.off('document:response', handler);
      const data =
        message.DocumentResponse ??
        message.QueryDocumentResponse ??
        message.DocumentRequestResponse ??
        message;
      resolve(data);
    };

    socketService.on('document:response', handler);

    socketService.emit('document:request', {
      messageId: generateID(),
      messageType: 'DocumentRequest',
      messageTimestamp: new Date().toISOString(),
      DocumentRequest: { action, ...extra },
    });
  });
}

/**
 * List signed documents for the Account tab.
 * Returns files with the signed_ prefix (signed_agreement.pdf + 3 reference docs).
 */
export async function getSignedAgreements(_organizationId?: string, _sector?: string): Promise<AgreementDocument[]> {
  try {
    const data = await sendDocumentRequest('LIST_SIGNED', {});
    const allDocs: any[] = data?.documents ?? data?.items ?? [];
    // Only show signed_ prefixed files in the Account tab
    const docs = allDocs.filter((d: any) => {
      const name = d.fileName ?? d.name ?? d.key ?? '';
      return name.startsWith('signed_');
    });

    return docs.map((doc: any, idx: number) => ({
      id: doc.id || doc.documentId || doc.key || `doc-${idx}`,
      documentType: doc.documentType ?? doc.type ?? 'Agreement',
      fileName: doc.fileName ?? doc.name ?? 'signed_agreement.pdf',
      agreementVersion: doc.agreementVersion ?? doc.version ?? '',
      signedBy: doc.signedBy ?? doc.signedByName ?? '',
      signedAt: doc.signedAt ?? doc.createdAt ?? '',
      downloadUrl: doc.downloadUrl ?? doc.url ?? '',
    }));
  } catch (error) {
    logger.error('[AgreementService] Failed to fetch signed agreements:', error);
    return [];
  }
}

/**
 * Fetch a presigned download URL for a signed document via GET_SIGNED.
 * Called on-demand when the user clicks View.
 */
export async function getSignedDocUrl(fileName: string): Promise<string> {
  try {
    const data = await sendDocumentRequest('GET_SIGNED', { fileName });
    return data?.url ?? data?.downloadUrl ?? '';
  } catch (error) {
    logger.error('[AgreementService] GET_SIGNED failed:', error);
    return '';
  }
}

/**
 * Fetch a presigned GET URL for a specific draft document via GET_DRAFT.
 */
export async function getDraftDocUrl(fileName: string): Promise<string> {
  try {
    const data = await sendDocumentRequest('GET_DRAFT', { fileName });
    return data?.url ?? data?.downloadUrl ?? '';
  } catch (error) {
    logger.error('[AgreementService] GET_DRAFT failed:', error);
    return '';
  }
}

let draftsCache: any[] | null = null;
let draftsCacheTs = 0;
const DRAFTS_CACHE_TTL = 10 * 60 * 1000;

export async function getAustracDocuments(folder?: string): Promise<S3Document[]> {
  try {
    const config = getRuntimeConfig();
    const baseUrl = config?.VITE_C1_API_URL;
    if (!baseUrl) throw new Error('C1 API URL not configured');

    if (!draftsCache || Date.now() - draftsCacheTs > DRAFTS_CACHE_TTL) {
      const res = await fetch(`${baseUrl}/agreements/documents/drafts`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      draftsCache = data?.documents ?? [];
      draftsCacheTs = Date.now();
    }

    const allFiles = draftsCache!;

    const prefix = folder ? `austrac/${folder}/` : 'austrac/';

    return allFiles
      .filter((f: any) => {
        const name: string = f.fileName ?? f.key ?? '';
        return name.startsWith(prefix) && !name.endsWith('/');
      })
      .map((f: any, idx: number) => ({
        key: f.fileName || f.key || `doc-${idx}`,
        fileName: (f.fileName || f.key || '').split('/').pop() ?? '',
        size: f.size ?? 0,
        lastModified: f.lastModified ?? '',
        downloadUrl: f.downloadUrl ?? '',
      }));
  } catch (error) {
    logger.error('[AgreementService] Failed to fetch AUSTRAC documents:', error);
    return [];
  }
}
